import { theme as HSDThemeProvider } from '@hopskipdrive/react-components';
import { createTheme, CssBaseline, StyledEngineProvider, ThemeProvider as MuiThemeProvider } from '@mui/material';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { createGlobalStyle } from 'styled-components';

import { colorPalette, colorTokens, variables } from './variables';

const theme = createTheme({
  ...HSDThemeProvider,
  spacing: 4,
  palette: {
    primary: {
      ...colorTokens.primary,
    },
    secondary: {
      ...colorTokens.secondary,
    },
    error: {
      ...colorTokens.error,
    },
    warning: {
      ...colorTokens.warning,
    },
    info: {
      ...colorTokens.info,
    },
    success: {
      ...colorTokens.success,
    },
  },
  typography: {
    fontFamily: "'DM Sans', Arial, 'sans-serif'",
    fontSize: 13,
    h1: {
      fontSize: 24,
      fontWeight: 700,
      margin: 0,
    },
    h2: {
      fontSize: 16,
      fontWeight: 700,
    },
    h3: {
      fontSize: 14,
      fontWeight: 700,
    },
    h4: {
      fontSize: 13,
      fontWeight: 700,
    },
    body1: {
      fontSize: 13,
      fontWeight: 500,
    },
    body2: {},
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      textTransform: 'uppercase',
      color: colorTokens.text.contrast,
    },
    bodyM: {
      fontSize: 13,
      fontWeight: 500,
    },
    bodyS: {
      fontSize: 13,
      fontWeight: 300,
    },
    labelXL: {
      fontSize: 18,
      fontWeight: 500,
    },
    labelL: {
      fontSize: 15,
      fontWeight: 500,
    },
    labelM: {
      fontSize: 14,
      fontWeight: 400,
    },
    labelS: {
      fontSize: 13,
      fontWeight: 500,
    },
    labelXS: {
      fontSize: 12,
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 500,
    },
  },
  components: {
    ...HSDThemeProvider.components,
    MuiCssBaseline: {
      ...HSDThemeProvider.components?.MuiCssBaseline,
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: colorTokens.primary.light,
          '&:hover': {
            color: colorTokens.primary.light,
          },
          '&:active': {
            textDecoration: 'none',
            color: colorTokens.primary.dark,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
          '&::-webkit-input-placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
          '&::-moz-placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
          '&:-ms-input-placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: '40px',
          background: colorTokens.background.default,
          border: `1px solid ${colorPalette.neutralVariant60}`,
          boxSizing: 'border-box',
          borderRadius: '84px',
          fontSize: '13px',
          color: colorTokens.text.medium,
          lineHeight: 1.4,
          svg: {
            color: `${colorTokens.action.active} !important`,
          },
          '&:hover': {
            border: `1px solid ${colorPalette.neutralVariant20} !important`,
            svg: {
              color: `${colorTokens.text.medium} !important`,
            },
          },
          '&.Mui-focused': {
            border: `1px solid ${colorPalette.blue50} !important`,
          },
          '&.Mui-focused svg': {
            color: `${colorTokens.info.main} !important`,
          },
          '&.Mui-disabled': {
            background: '#EAEFF2',
            border: `1px solid ${colorPalette.neutralVariant90}`,
            pointerEvents: 'none',
          },
          '&.Mui-error': {
            borderColor: colorPalette.red45,
            background: colorTokens.background.default,
          },
        },
        input: {
          paddingTop: 0,
          paddingBottom: 0,
          '&::placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
        },
        multiline: {
          padding: '10px 16px',
          borderRadius: '20px',
        },
        notchedOutline: {
          display: 'none',
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: colorTokens.primary.main,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingTop: '8px',
          paddingBottom: '8px',
          fontWeight: 300,
          '&.selected': {
            background: 'none',
            color: colorTokens.primary.dark,
            fontWeight: 500,
            '&:hover': {
              background: colorTokens.action.hover,
            },
          },
          '&:hover': {
            background: colorTokens.action.hover,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '20px 30px',
          borderTop: `1px solid ${colorPalette.neutralVariant90}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          padding: '16px',
          borderBottom: `1px solid ${colorPalette.neutralVariant90}`,
          svg: {
            color: colorTokens.action.active,
            textDecoration: 'none',
            '&:hover': {
              color: colorTokens.text.medium,
            },
          },
        },
        head: {
          padding: '10px 26px',
          fontSize: 14,
          fontWeight: 600,
          color: `${colorTokens.text.medium}`,
          // Shared table labels can render Text with its own body typography.
          '& .MuiTypography-root': {
            fontSize: 'inherit',
            fontWeight: 'inherit',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          color: colorPalette.red20,
          background: 'rgb(255, 239, 238)',
          svg: {
            color: colorTokens.error.main,
          },
        },
        standardWarning: {
          color: colorTokens.warning.main,
        },
        standardInfo: {
          color: colorPalette.blue30,
          svg: {
            color: colorTokens.info.main,
          },
        },
        filledInfo: {
          backgroundColor: colorTokens.info.dark,
        },
        filledSuccess: {
          backgroundColor: colorTokens.success.dark,
        },
        filledWarning: {
          backgroundColor: colorPalette.orange35,
        },
        filledError: {
          backgroundColor: colorPalette.red30,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          display: 'inline-flex',
          padding: '7px 15px',
          height: '34px',
          borderRadius: '17px',
          gap: '2px',
          fontSize: '13px',
          backgroundColor: colorTokens.background.light,
          color: colorTokens.primary.main,
        },
        label: {
          lineHeight: '18px',
          color: 'currentColor',
          padding: 0,
          userSelect: 'none',
        },
        sizeSmall: {
          height: '26px',
          padding: '4px 8px',
          fontSize: '12px',
        },
        deleteIcon: {
          color: colorTokens.primary.main,
          '&:hover': {
            color: colorTokens.primary.light,
          },
          '&:focus': {
            color: `${colorTokens.text.dark} !important`,
          },
        },
      },
    },
    MuiTabs: {
      ...HSDThemeProvider.components?.MuiTabs,
      styleOverrides: {
        ...HSDThemeProvider.components?.MuiTabs?.styleOverrides,
        root: [
          HSDThemeProvider.components?.MuiTabs?.styleOverrides?.root,
          {
            // Outrank the shared indicator sx and consumer tab state selectors without changing geometry.
            '&& .MuiTabs-indicator': {
              backgroundColor: colorPalette.brandNavy,
            },
            '&& .MuiTab-root:not(.Mui-disabled):is(.Mui-selected, :hover, :focus, .Mui-focusVisible)': {
              color: colorPalette.brandNavy,
            },
            '&& .MuiTab-root:not(.Mui-disabled):is(:focus-visible, .Mui-focusVisible)': {
              outline: `2px solid ${colorPalette.brandNavy}`,
              outlineOffset: '-2px',
              '@media (forced-colors: active)': {
                outlineColor: 'CanvasText',
              },
            },
          },
        ],
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          position: 'relative',
          fontSize: '16px',
          textTransform: 'none',
          padding: 0,
          margin: '0 16px 0 17px',
          overflow: 'unset',
          color: colorTokens.text.medium,
          opacity: 1,
          minHeight: '18px',
          minWidth: 0,
          '&:before': {
            content: "''",
            position: 'absolute',
            width: '1px',
            height: '13px',
            background: '#ccc',
            right: '-17px',
            transform: 'translate(50%, 0)',
          },
          '&:last-child': {
            paddingRight: 0,
            '&:before': {
              display: 'none',
            },
          },
          '&.Mui-disabled': {
            color: colorPalette.neutralVariant60,
            opacity: 0.5,
          },
          '&.Mui-selected': {
            color: colorTokens.text.hover,
          },
          '&:not(.Mui-selected):hover,&:not(.Mui-selected):focus': {
            color: colorTokens.text.hoverInactive,
          },
          button: {
            minHeight: '18px',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': {
            border: `1px solid ${colorPalette.neutral100}`,
            boxShadow: 'none',
            outline: `2px solid ${colorTokens.text.hover}`,
          },
          '&:active,focus': {
            outline: 'none',
          },
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontSize: '13px',
          fontWeight: 700,
          lineHeight: '175%',
          letterSpacing: '0.15px',

          '&:focus': {
            color: colorTokens.text.hover,
            border: `1px solid ${colorPalette.neutral100}`,
            outline: `2px solid ${colorTokens.text.hover}`,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          input: {
            '& + svg': {
              color: `${colorPalette.blue65} !important`,
            },
            '&:hover + svg': {
              color: `${colorTokens.secondary.dark} !important`,
            },
            '&:disabled + svg': {
              color: `${colorTokens.text.disabled} !important`,
            },
            '&:focus + svg': {
              borderRadius: '4px',
              outline: `2px solid ${colorTokens.text.hover} !important`,
            },
          },
        },
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          button: {
            '&:focus': {
              boxShadow: 'none',
              outline: `2px solid ${colorTokens.text.hover} !important`,
            },
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          input: {
            '& + svg': {
              color: `${colorPalette.blue65} !important`,
            },
            '&:hover + svg': {
              color: `${colorTokens.secondary.dark} !important`,
            },
            '&:disabled + svg': {
              color: `${colorTokens.text.disabled} !important`,
            },
            '&:focus + svg': {
              borderRadius: '4px',
              outline: `2px solid ${colorTokens.text.hover} !important`,
            },
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiAutocomplete-clearIndicator svg': {
            color: `${colorTokens.action.active} !important`,
          },
        },
        input: {
          '&::placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
        },
        listbox: {
          '& .MuiAutocomplete-option': {
            '&.Mui-focusVisible': {
              borderRadius: '4px',
              border: `2px solid ${colorPalette.navy50} !important`,
              outline: `2px solid ${colorTokens.text.contrast} !important`,
            },
          },
        },
        paper: {
          fontWeight: 400,
          borderRadius: '20px',
          padding: '6px 0px',
          boxShadow: '0px 4px 15px rgba(77, 88, 147, 0.2)',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '12px',
          lineHeight: 'normal',
          fontWeight: 300,
          '&.Mui-error': {
            color: colorTokens.error.dark,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          height: '40px',
          background: colorTokens.background.default,
          border: `1px solid ${colorPalette.neutralVariant60}`,
          boxSizing: 'border-box',
          borderRadius: '84px',
          fontSize: '13px',
          color: colorTokens.text.medium,
          lineHeight: 1.4,
          '&:hover': {
            color: colorTokens.text.dark,
            border: `1px solid ${colorPalette.neutralVariant20} !important`,
            svg: {
              color: `${colorTokens.text.medium} !important`,
            },
          },
          '&.Mui-focused': {
            border: `1px solid ${colorPalette.blue50} !important`,
          },
          '&.Mui-focused svg': {
            color: `${colorTokens.info.main} !important`,
          },
          '&.Mui-disabled': {
            background: '#EAEFF2',
            border: `1px solid ${colorPalette.neutralVariant90}`,
            pointerEvents: 'none',
          },
          '&.Mui-error, hasError': {
            borderColor: colorPalette.red45,
            background: colorTokens.background.default,
          },
          svg: {
            width: '0.8em',
            marginRight: '2px',
            marginLeft: '2px',
            color: `${colorTokens.action.active} !important`,
          },
        },
        sectionsContainer: {
          minWidth: '100px',
          width: '6.25rem',
        },
        input: {
          paddingTop: 0,
          paddingBottom: 0,
          '&::placeholder': {
            color: colorTokens.text.medium,
            opacity: 1,
          },
        },
        notchedOutline: {
          display: 'none',
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          borderRadius: '84px',
          '&.Mui-error': {
            border: `1px solid ${colorPalette.red45}`,
          },
        },
      },
    },
    ...variables,
  },
});

const GlobalStyle = createGlobalStyle`
  html {
    scrollbar-gutter: stable;
  }

  body {
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    font-family: 'CentraNo2', Arial, Helvetica, sans-serif;
  }
  body, html {
    height: 100%;
  }
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }
`;

const ThemeProvider = ({ children }): JSX.Element => (
  <StyledEngineProvider injectFirst>
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyle />
      {children}
    </MuiThemeProvider>
  </StyledEngineProvider>
);

export { theme, ThemeProvider };
